import Bed from '../assets/bed.svg'
import Blanket from '../assets/blanket.svg'
import Book from '../assets/book.svg'
import Caffeine from '../assets/caffeine.svg'
import Clock from '../assets/clock.svg'
import Curtain from '../assets/curtains.svg'
import Exit from '../assets/exit.svg'
import Lamp from '../assets/lamp.svg'
import OrangeScreen from '../assets/orange-screen.svg'
import Pills from '../assets/pills.svg'
import Target from '../assets/target.svg'
import {
  BlackoutCurtains,
  LightTherapyLamp,
  Melatonin,
} from '../components/affiliates/Sleep'
import { Footnote } from '../components/curriculum/Footnote'
import { FootnoteList } from '../components/curriculum/FootnoteList'
import { Divider } from '../components/divider/Divider'
import { Explanation } from '../components/explanation/Explanation'
import { Section } from '../components/guides/Section'
import { Title } from '../components/guides/Title'
import { Layout } from '../components/Layout'

const bodyCompositionAndSleepLinks = [
  'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC2951287/',
  'https://www.facebook.com/MennoHenselmans/posts/2984166111641204',
  'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC2951287/',
]

const Sleep = () => {
  return (
    <Layout>
      <div className={'w90 center module'}>
        <h1 className="huge">Sleep</h1>
        <Explanation color="blue">
          <div>
            <strong>Why: </strong>
            Reduce your risk of stuff like depression and death{' '}
            <FootnoteList
              links={[
                'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC5449130/',
                'https://www.ahajournals.org/doi/full/10.1161/JAHA.117.005947',
              ]}
            />
            . Also, lose less muscle{' '}
            <FootnoteList links={bodyCompositionAndSleepLinks} />.
          </div>
        </Explanation>

        <div>
          {/* Blackout curtains  */}
          <div>
            <h2 className="mb0 mt40">Setup</h2>

            <Section>
              <Title
                alt="Curtain"
                src={Blanket}
                title={'Temperature: 60 to 68˙F'}
              />
              <Explanation color="blue">
                <div>
                  <strong>Why:</strong> Cold temperatures are associated with
                  better sleep{' '}
                  <Footnote link={'https://pubmed.ncbi.nlm.nih.gov/9322266/'} />
                  . So crank up the AC and fan!
                </div>
              </Explanation>
            </Section>

            <Section>
              <Title alt="Curtain" src={Curtain} title={'Blackout curtains'} />
              <Explanation color="blue">
                <div>
                  <strong>Why:</strong> Light suppresses melatonin, the hormone
                  you need to sleep{' '}
                  <Footnote
                    link={'https://pubmed.ncbi.nlm.nih.gov/30311830/'}
                  />
                  . This makes a set of{' '}
                  <BlackoutCurtains text="$25 blackout curtains" /> an excellent
                  way to improve your sleep quality.
                </div>
              </Explanation>
              <Explanation color="green">
                <div>
                  <strong>Tip:</strong> Your bedroom should be so dark that you
                  cannot see your hands!
                </div>
              </Explanation>
            </Section>

            <Section>
              <Title alt="Pills" src={Pills} title={'Melatonin'} />
              <Explanation color="blue">
                <div>
                  <strong>Why:</strong> Melatonin is a foolproof way of making
                  yourself sleepy{' '}
                  <FootnoteList
                    links={[
                      'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC3656905/',
                      'https://www.sciencedirect.com/science/article/abs/pii/S1087079204000607',
                    ]}
                  />
                  . This is
                  <strong> really</strong> useful if you have been going to bed
                  at 3:00am and would like to switch to a more normal sleep
                  schedule, like 10:00pm. You can buy a set of{' '}
                  <Melatonin text="90 pills for $4" />.
                </div>
              </Explanation>

              <Explanation color="yellow">
                <div>
                  <strong>Heads up:</strong> Melatonin is safe and
                  non-addictive, and thus can be taken indefinitely{' '}
                  <FootnoteList
                    links={[
                      'https://www.healthline.com/nutrition/melatonin-side-effects#section8',
                    ]}
                  />
                  . However, the dosages of melatonin sold in stores is way too
                  high: usually 3 to 5 mg. This is about{' '}
                  <strong>10 times</strong> as much as the brain normally
                  releases{' '}
                  <Footnote
                    link={
                      'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC4273450/'
                    }
                  />
                  . That is why we recommend a melatonin with a dosage of 300
                  mcg{' '}
                  <Footnote
                    link={'https://examine.com/supplements/melatonin/'}
                  />
                  .
                </div>
              </Explanation>
              <Explanation color="green">
                <div>
                  <strong>Tip:</strong> Most people take melatonin way too late.
                  It takes roughly 2 hours after taking melatonin before it
                  starts to make you drowsy{' '}
                  <Footnote
                    link={
                      'https://www.health.com/condition/sleep/is-it-safe-to-take-melatonin-every-night'
                    }
                  />
                  . Thus, taking melatonin right before bedtime will{' '}
                  <strong>not</strong> help you fall asleep. In fact, it will
                  actually make you sleepier during the day!
                </div>
              </Explanation>
            </Section>
          </div>

          <Section>
            <Title
              alt="Phone with orange screen"
              src={OrangeScreen}
              title={'Blue light filter'}
            />
            <Explanation color="blue">
              <div>
                <strong>Why:</strong> Blue light is especially good at
                suppressing melatonin{' '}
                <Footnote
                  link={
                    'https://www.health.harvard.edu/staying-healthy/blue-light-has-a-dark-side'
                  }
                />
                , the hormone you need to sleep.
              </div>
            </Explanation>
            <Explanation color="green">
              <div>
                <strong>Tip:</strong> Decrease the amount of blue light coming
                from your devices. If you have an iPhone, this is handled by
                Night Shift. If you have an Android Phone, this is handled by
                Night Light. If you have a Mac or PC, this is handled by the
                free program f.lux, which you can download{' '}
                <a
                  target="blank"
                  rel="noopener noreferrer"
                  href="https://justgetflux.com/"
                >
                  here
                </a>
                .
              </div>
            </Explanation>
            <Explanation color="yellow">
              <div>
                <strong>Heads up:</strong> As a rule, you want to configure
                these programs to use the <strong>warmest</strong> setting
                possible, as this will eliminate the most blue light!
              </div>
            </Explanation>
          </Section>

          <Section>
            <Title alt="Lamp" src={Lamp} title={'Light therapy lamp'} />
            <Explanation color="blue">
              <div>
                <strong>Why:</strong> Humans don{`'`}t get enough sunlight,
                especially during the winter. Thus, there is evidence that light
                therapy lamps, which emit a bright light, can help with
                depression, especially the seasonal variety{' '}
                <FootnoteList
                  links={[
                    'https://pubmed.ncbi.nlm.nih.gov/26580307/',
                    'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC6746555/',
                    'https://meaningness.com/metablog/sad-light-lumens',
                    'http://www.columbia.edu/~mt12/blt.htm',
                  ]}
                />
                . Light therapy can also help you stay awake longer or wake up
                earlier. Thus, it{`'`}s worth spending{' '}
                <LightTherapyLamp text="$21 on a small lamp" /> to see if it
                helps.
              </div>
            </Explanation>
            <Explanation color="green">
              <div>
                <strong>Tip: </strong> If you are interested in light therapy,
                buy a lamp with <strong>10,000 lux</strong>.{' '}
                <strong>Lumen</strong> is the total amount of light emitted by a
                lamp, while
                <strong> lux</strong> is how much light you actually {`"`}get
                {`"`}.
              </div>
            </Explanation>
          </Section>
        </div>

        <Divider className="mb20 mt50" height={4} />

        <div>
          <h2 className="mb0">Program</h2>

          <Section>
            <Title
              alt="Target"
              src={Target}
              title={'Set your target: 9 hours'}
            />
            <Explanation color="blue">
              <div>
                <strong>Why:</strong> 7 hours of continuous sleep{' '}
                <Footnote link="https://www.theatlantic.com/science/archive/2015/10/the-many-myths-of-paleo-sleeping/410707/" />{' '}
                is <strong>minimum</strong> amount of sleep required for an
                adult{' '}
                <FootnoteList
                  className={'closer'}
                  links={[
                    'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC2910533/',
                    'https://www.sciencedirect.com/science/article/pii/S1087079217300278',
                    'https://www.cell.com/current-biology/fulltext/S0960-9822(15)01157-4',
                  ]}
                />
                . However, 9 hours is likely the correct amount for optimal
                functioning, <strong>especially if you exercise</strong>{' '}
                <FootnoteList
                  links={[
                    'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC6988893/',
                    'https://www.ncbi.nlm.nih.gov/pubmed/21715510',
                    'https://www.ncbi.nlm.nih.gov/pubmed/23779051',
                    'https://pubmed.ncbi.nlm.nih.gov/17625932/',
                  ]}
                />
                .
              </div>
            </Explanation>
          </Section>

          <Section>
            <Title
              alt="Clock"
              src={Clock}
              title={'Set a consistent schedule'}
            />
            <Explanation color="blue">
              <div>
                <strong>Why:</strong> In some studies, the{' '}
                <strong>regularity</strong> of your sleep, both in terms of
                timing and duration, matters more for conditions like diabetes
                and depression than the total duration of your sleep{' '}
                <FootnoteList
                  className={'semi-spaced'}
                  links={[
                    'https://www.nature.com/articles/s41746-021-00400-z',
                    'https://www.ncbi.nlm.nih.gov/pubmed/31167888',
                    'https://pubmed.ncbi.nlm.nih.gov/23967186/',
                  ]}
                />
                . Thus, a consistent sleep wake-up time and bedtime (including
                on weekends), enforced by an alarm, is a really good thing.
              </div>
            </Explanation>
            <Explanation color="green">
              <div>
                <strong>Tip: </strong> To make it easier to set a consistent
                sleep schedule, you can use melatonin and/or a light therapy
                lamp, both covered below.
              </div>
            </Explanation>
          </Section>

          <Section>
            <Title
              alt="Cup of coffee"
              src={Caffeine}
              title={'Caffeine: Stop after lunch'}
            />
            <Explanation color="blue">
              <div>
                <strong>Why:</strong> Caffeine lasts a surprisingly long time in
                the body, ~ 10 - 12 hours{' '}
                <Footnote
                  link={'https://www.ncbi.nlm.nih.gov/books/NBK223808'}
                />{' '}
                and will mess up your sleep if still present in the body in
                significant quantities. It does this by preventing
                <strong> adenosine</strong>, a molecule that accumulates
                throughout the day and makes you drowsy, from binding to its
                receptor{' '}
                <Footnote link="https://stm.sciencemag.org/content/7/305/305ra146" />
                .
              </div>
            </Explanation>
            <Explanation color="yellow">
              <div>
                <strong>Heads up: </strong> The body will make more adenosine
                receptors to compensate for chronic caffeine use, leading to
                tolerance{' '}
                <Footnote link="https://medium.com/science-technology/this-is-what-caffeine-is-doing-to-your-brain-1ec89c4ecf51" />
                . If you want to eliminate that tolerance, reducing your intake
                by half every three days until you are caffeine free{' '}
                <Footnote link="https://lifehacker.com/reset-your-caffeine-tolerance-by-halving-your-intake-ev-1784849271" />
                .
              </div>
            </Explanation>
          </Section>

          <Section>
            <Title alt="Pills" src={Pills} title={'Melatonin: Optional'} />
            <Explanation color="blue">
              <div>
                <strong>Why:</strong> Some people go to bed late, like 3:00 am,
                and wake up equally late, like 11:00am, and are fine with it.
                Others would rather shift their sleep cycle{' '}
                <strong>earlier</strong>. The best way to do that is to take
                melatonin two hours <strong>before</strong> your ideal bedtime{' '}
                <strong>and</strong> to set an alarm for your ideal wake up
                time.
              </div>
            </Explanation>
            <Explanation color="green">
              <div>
                <strong>Tip:</strong> If you have access to a light therapy lamp
                (next section), you should expose yourself to it for 30 minutes
                after you first wake up. This will synergize with the melatonin
                you take, making it even easier to shift your sleep schedule to
                your ideal wake up time{' '}
                <Footnote
                  link={
                    'https://www.sunbox.com/research/sleep-and-circadian-rhythm-disorder/'
                  }
                />
                .
              </div>
            </Explanation>
            <Explanation color="yellow">
              <div>
                <strong>Heads up:</strong> During the first couple days of your
                transition, you{`'`}re going to be tired, especially if the
                transition is particularly brutal, like going from 11:00 am to
                7:00 am. <strong>But this sleepiness is good</strong>. It
                ensures you will be sleepy enough to fall asleep at 10:00 pm,
                rather than say, 2:00 pm. Thus,{' '}
                <strong>it is vital you do not nap</strong>, as that will
                diminish this sleepiness!
              </div>
            </Explanation>
          </Section>

          <Section>
            <Title alt="Lamp" src={Lamp} title={'Light therapy: Optional'} />
            <Explanation color="blue">
              <div>
                <strong>Why:</strong> Some people go to bed early, like 8:00 pm,
                and wake up equally early, like 3:00am, and are fine with it.
                Others would rather shift their sleep cycle{' '}
                <strong>later</strong>. The best way to do that is to expose
                yourself to <strong>30 minutes</strong> of bright light{' '}
                <strong>three hours</strong> before you go to sleep{' '}
                <Footnote
                  link={
                    'https://www.sunbox.com/research/sleep-and-circadian-rhythm-disorder/'
                  }
                />
                .
              </div>
            </Explanation>
          </Section>

          <Section>
            <Title alt="Bed" src={Bed} title={'Get in bed when sleepy'} />
            <Explanation color="blue">
              <div>
                <strong>Why:</strong> One of the biggest mistakes people make is
                getting into bed <strong>before they are sleepy</strong>, as
                they just end up lying in bed, unable to sleep. Eventually, the
                brain learns to associate the bed with{' '}
                <strong>not sleeping</strong> and that is how you get insomnia{' '}
                <Footnote
                  link={
                    'https://www.verywellhealth.com/30-days-to-better-sleep-go-to-bed-only-when-sleepy-3973903'
                  }
                />
                .
              </div>
            </Explanation>
            <Explanation color="green">
              <div>
                <strong>Tip:</strong> You know you{`'`}re{' '}
                <strong>sleepy</strong>, rather than just{' '}
                <strong>tired or fatigued</strong>, when you find yourself
                struggling to stay awake{' '}
                <Footnote link="https://www.quickanddirtytips.com/health-fitness/mental-health/sleep-hygiene-insomnia" />
                . You have a hard time concentrating on your book or phone, your
                eyes keep drifting shut, and you{`'`}re yawning... a lot!
              </div>
            </Explanation>
            <Explanation color="yellow">
              <div>
                <strong>Heads up:</strong> You should actively avoid the bedroom
                <strong> unless you are trying to sleep or having sex</strong>.
                The last thing you want to do is hang out in bed all day,
                destroying the brain{`'`}s association between sleep and the
                bedroom.
              </div>
            </Explanation>
          </Section>

          <Section>
            <Title alt="Book" src={Book} title={'Wind down'} />
            <Explanation color="blue">
              <div>
                <strong>Why:</strong> To fall asleep, you need to be relaxed,
                not stimulated{' '}
                <Footnote
                  link={
                    'https://medium.com/s/story/combat-tested-training-unwind-and-sleep-anywhere-in-120-seconds-27d5307b7606'
                  }
                />
                . So, once you feel sleepy (above) and have transitioned to the
                bed, do something relaxing, like reading a book{' '}
                <Footnote link="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC2675894/" />
                . Do this until you feel so <strong>sleepy</strong> that you can
                barely keep your eyes open. Then, stop and go to sleep!
              </div>
            </Explanation>

            <Explanation color="yellow">
              <div>
                <strong>Heads up:</strong> Your phone or tablet{' '}
                <strong>can</strong> be used for the sleep ritual, but it{' '}
                <strong>really</strong> depends on what you use it for. For
                example, I would not recommend reading the news, playing games,
                or scrolling through Facebook or Twitter, as the feelings of
                self-comparison, anger, and anxiety created by these apps are
                too stimulating. In contrast, a TikTok, Reddit, or Instagram
                stream focused on things like cute animals, relaxing imagery,
                funny memes/videos, etc. could be an excellent option!
              </div>
            </Explanation>
          </Section>

          <Section>
            <Title alt="Exit" src={Exit} title={'Leave the bedroom'} />
            <Explanation color="blue">
              <div>
                <strong>Why:</strong> If you cannot fall asleep within{' '}
                <strong>20 minutes</strong>, you should leave the bedroom and do
                something else until you feel sleepy{' '}
                <Footnote
                  link={
                    'https://health.clevelandclinic.org/5-strategies-that-will-help-you-get-back-to-sleep/'
                  }
                />
                . This is so you can preserve the association between the
                bedroom and sleep, while also minimizing the frustration that
                comes with tossing and turning!
              </div>
            </Explanation>
          </Section>
        </div>
      </div>
    </Layout>
  )
}

export default Sleep
